import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiFetch from "./utils/api";
import "./ReportsPage.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

ModuleRegistry.registerModules([AllCommunityModule]);

function safeArray(v, key) {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v[key])) return v[key];
  return [];
}

function buildLookup(rows, idKey, codeKey) {
  const byId = {};
  const byCode = {};
  (rows || []).forEach((r) => {
    const id = r && r[idKey];
    const code = r && r[codeKey];
    if (id !== null && typeof id !== "undefined") byId[String(id)] = r;
    if (code !== null && typeof code !== "undefined")
      byCode[String(code).toLowerCase()] = r;
  });
  return { byId, byCode };
}

export default function AnalyticsPage({ user }) {
  const gridRef = useRef(null);
  const fieldMenuRef = useRef(null);
  const defaultYear = new Date().getFullYear();
  const [year, setYear] = useState(defaultYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [showFieldMenu, setShowFieldMenu] = useState(false);
  const [visibleFields, setVisibleFields] = useState([
    "TimesheetID",
    "TimesheetDate",
    "TimesheetHours",
    "ProjectName",
    "ActivityName",
  ]);
  const [userOptions, setUserOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!user || !user.UserCode) return;
    setSelectedUsers((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          id: user.UserID,
          code: user.UserCode,
          desc: user.UserDescription || user.UserCode,
        },
      ];
    });
  }, [user]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await apiFetch("/api/projects");
        const rows = safeArray(p, "projects");
        if (mounted) {
          setProjectOptions(rows);
        }
      } catch (e) {}

      try {
        const a = await apiFetch("/api/activities");
        const rows = safeArray(a, "activities");
        if (mounted) {
          setActivityOptions(rows);
        }
      } catch (e) {}

      try {
        const u = await apiFetch("/api/users");
        const rows = safeArray(u, "users");
        if (mounted) {
          setUserOptions(rows);
        }
      } catch (e) {
        if (mounted && user && user.UserCode) {
          setUserOptions([
            {
              UserID: user.UserID,
              UserCode: user.UserCode,
              UserDescription: user.UserDescription || user.UserCode,
              UserRole: user.UserRole || "",
              UserActive: user.UserActive,
              UserManager: user.UserManager || null,
              UserDailyHours: user.UserDailyHours ?? null,
              UserShiftMask: user.UserShiftMask ?? null,
            },
          ]);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  const baseColumnDefs = useMemo(
    () => [
      {
        field: "TimesheetID",
        headerName: "TimesheetID",
        filter: "agNumberColumnFilter",
        minWidth: 120,
      },
      { field: "TimesheetDate", headerName: "TimesheetDate", minWidth: 140 },
      {
        field: "TimesheetHours",
        headerName: "TimesheetHours",
        filter: "agNumberColumnFilter",
        minWidth: 140,
        valueFormatter: (p) => Number(p.value || 0).toFixed(2),
      },
      {
        field: "UserID",
        headerName: "UserID",
        filter: "agNumberColumnFilter",
        minWidth: 110,
      },
      { field: "UserCode", headerName: "UserCode", minWidth: 140 },
      {
        field: "UserDescription",
        headerName: "UserDescription",
        minWidth: 220,
      },
      { field: "UserRole", headerName: "UserRole", minWidth: 150 },
      { field: "UserActive", headerName: "UserActive", minWidth: 120 },
      { field: "UserManager", headerName: "UserManager", minWidth: 150 },
      {
        field: "UserDailyHours",
        headerName: "UserDailyHours",
        filter: "agNumberColumnFilter",
        minWidth: 150,
      },
      {
        field: "UserShiftMask",
        headerName: "UserShiftMask",
        minWidth: 150,
      },
      { field: "ProjectName", headerName: "ProjectName", minWidth: 240 },
      { field: "ActivityName", headerName: "ActivityName", minWidth: 220 },
      {
        field: "ProjectID",
        headerName: "ProjectID",
        filter: "agNumberColumnFilter",
        minWidth: 120,
      },
      { field: "ProjectCode", headerName: "ProjectCode", minWidth: 140 },
      {
        field: "ProjectDescription",
        headerName: "ProjectDescription",
        minWidth: 240,
      },
      { field: "ProjectClient", headerName: "ProjectClient", minWidth: 150 },
      {
        field: "ProjectDivision",
        headerName: "ProjectDivision",
        minWidth: 160,
      },
      {
        field: "ProjectActive",
        headerName: "ProjectActive",
        minWidth: 130,
      },
      {
        field: "ProjectManager",
        headerName: "ProjectManager",
        minWidth: 150,
      },
      {
        field: "ProjectEstTime",
        headerName: "ProjectEstTime",
        filter: "agNumberColumnFilter",
        minWidth: 150,
      },
      {
        field: "ProjectValue",
        headerName: "ProjectValue",
        filter: "agNumberColumnFilter",
        minWidth: 140,
      },
      {
        field: "ProjectIsResearchOrDevelopment",
        headerName: "ProjectIsResearchOrDevelopment",
        minWidth: 230,
      },
      {
        field: "ProjectIsChargable",
        headerName: "ProjectIsChargable",
        minWidth: 170,
      },
      {
        field: "ActivityID",
        headerName: "ActivityID",
        filter: "agNumberColumnFilter",
        minWidth: 120,
      },
      { field: "ActivityCode", headerName: "ActivityCode", minWidth: 140 },
      {
        field: "ActivityDescription",
        headerName: "ActivityDescription",
        minWidth: 240,
      },
      {
        field: "ActivityActive",
        headerName: "ActivityActive",
        minWidth: 130,
      },
      {
        field: "TimesheetDivision",
        headerName: "TimesheetDivision",
        minWidth: 160,
      },
      { field: "TimesheetNotes", headerName: "TimesheetNotes", minWidth: 280 },
    ],
    [],
  );

  const fieldOptions = useMemo(
    () =>
      baseColumnDefs.map((c) => ({
        field: c.field,
        label: c.headerName || c.field,
      })),
    [baseColumnDefs],
  );

  const columnDefs = useMemo(
    () =>
      baseColumnDefs.map((c) => ({
        ...c,
        hide: !visibleFields.includes(c.field),
      })),
    [baseColumnDefs, visibleFields],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 110,
      floatingFilter: true,
    }),
    [],
  );

  const loadAnalytics = useCallback(
    async (y = year) => {
      setLoading(true);
      setError("");
      try {
        const hasYear = String(y || "").trim() !== "";
        const start = hasYear ? `${y}-01-01` : "";
        const end = hasYear ? `${y}-12-31` : "";

        let timesheets = [];
        try {
          const all = await apiFetch(
            hasYear
              ? `/api/timesheets?start=${start}&end=${end}`
              : "/api/timesheets",
          );
          timesheets = safeArray(all, "records");
        } catch (e) {
          const mine = await apiFetch(
            hasYear
              ? `/api/timesheets/me?StartDate=${start}&EndDate=${end}`
              : "/api/timesheets/me",
          );
          timesheets = safeArray(mine, "records");
        }

        const projectsBy = buildLookup(projectOptions, "id", "code");
        const activitiesBy = buildLookup(activityOptions, "id", "code");
        const usersBy = buildLookup(userOptions, "UserID", "UserCode");

        const rows = (timesheets || []).map((t) => {
          const date = new Date(t.TimesheetDate || t.Date || null);
          const validDate = !isNaN(date.getTime());

          const project =
            projectsBy.byId[String(t.ProjectID)] ||
            projectsBy.byCode[String(t.ProjectCode || "").toLowerCase()] ||
            null;
          const activity =
            activitiesBy.byId[String(t.ActivityID)] ||
            activitiesBy.byCode[String(t.ActivityCode || "").toLowerCase()] ||
            null;
          const matchedUser =
            usersBy.byId[String(t.UserID)] ||
            usersBy.byCode[String(t.UserCode || "").toLowerCase()] ||
            null;

          return {
            TimesheetID: t.TimesheetID,
            TimesheetDate: validDate ? date.toISOString().slice(0, 10) : "",
            TimesheetHours: Number(t.TimesheetHours || 0),
            UserID: t.UserID ?? matchedUser?.UserID ?? null,
            UserCode:
              t.UserCode ||
              matchedUser?.UserCode ||
              (user && user.UserCode) ||
              "",
            UserDescription:
              matchedUser?.UserDescription ||
              user?.UserDescription ||
              t.UserCode ||
              "",
            UserRole: matchedUser?.UserRole || "",
            UserActive:
              typeof matchedUser?.UserActive !== "undefined"
                ? matchedUser.UserActive
                : "",
            UserManager: matchedUser?.UserManager || "",
            UserDailyHours: matchedUser?.UserDailyHours ?? null,
            UserShiftMask: matchedUser?.UserShiftMask ?? null,
            ProjectName: project?.desc || t.ProjectCode || "",
            ProjectDescription: project?.desc || "",
            ActivityName: activity?.desc || t.ActivityCode || "",
            ActivityDescription: activity?.desc || "",
            ProjectCode: t.ProjectCode || project?.code || "",
            ActivityCode: t.ActivityCode || activity?.code || "",
            TimesheetDivision: t.TimesheetDivision || project?.division || "",
            TimesheetNotes: t.TimesheetNotes || "",
            ProjectID: t.ProjectID ?? project?.id ?? null,
            ActivityID: t.ActivityID ?? activity?.id ?? null,
            ProjectClient: project?.client || "",
            ProjectDivision: project?.division || "",
            ProjectActive:
              typeof project?.active !== "undefined" ? project.active : "",
            ProjectManager: project?.manager || "",
            ProjectEstTime: project?.ProjectEstTime ?? null,
            ProjectValue: project?.ProjectValue ?? null,
            ProjectIsResearchOrDevelopment:
              typeof project?.isResearchOrDevelopment !== "undefined"
                ? project.isResearchOrDevelopment
                : "",
            ProjectIsChargable:
              typeof project?.isChargable !== "undefined"
                ? project.isChargable
                : "",
            ActivityActive:
              typeof activity?.active !== "undefined" ? activity.active : "",
          };
        });

        rows.sort((a, b) =>
          String(a.TimesheetDate).localeCompare(String(b.TimesheetDate)),
        );
        setRawRows(rows);
      } catch (e) {
        setError(
          e && e.message ? e.message : "Failed to load analytics report",
        );
        setRawRows([]);
      } finally {
        setLoading(false);
      }
    },
    [year, projectOptions, activityOptions, userOptions, user],
  );

  useEffect(() => {
    loadAnalytics(year);
  }, [year, loadAnalytics]);

  const filteredRows = useMemo(() => {
    const rows = (rawRows || []).filter((r) => {
      if (startDate && String(r.TimesheetDate || "") < startDate) {
        return false;
      }

      if (endDate && String(r.TimesheetDate || "") > endDate) {
        return false;
      }

      if (selectedUsers.length > 0) {
        const hasUser = selectedUsers.some(
          (u) =>
            (u.id !== null &&
              typeof u.id !== "undefined" &&
              String(u.id) === String(r.UserID)) ||
            (u.code &&
              String(u.code).toLowerCase() ===
                String(r.UserCode || "").toLowerCase()),
        );
        if (!hasUser) return false;
      }

      if (selectedProjects.length > 0) {
        const hasProject = selectedProjects.some(
          (p) =>
            String(p.code).toLowerCase() ===
            String(r.ProjectCode || "").toLowerCase(),
        );
        if (!hasProject) return false;
      }

      if (selectedActivities.length > 0) {
        const hasActivity = selectedActivities.some(
          (a) =>
            String(a.code).toLowerCase() ===
            String(r.ActivityCode || "").toLowerCase(),
        );
        if (!hasActivity) return false;
      }

      return true;
    });

    return rows;
  }, [
    rawRows,
    startDate,
    endDate,
    selectedUsers,
    selectedProjects,
    selectedActivities,
  ]);

  useEffect(() => {
    if (!showFieldMenu) return;

    const onDocPointer = (e) => {
      const root = fieldMenuRef.current;
      if (!root) return;
      if (!root.contains(e.target)) {
        setShowFieldMenu(false);
      }
    };

    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
    };
  }, [showFieldMenu]);

  const toggleField = (field) => {
    setVisibleFields((prev) => {
      if (prev.includes(field)) return prev.filter((f) => f !== field);
      return [...prev, field];
    });
  };

  const addSelectedUser = (userId) => {
    if (!userId) return;
    const u = userOptions.find((x) => String(x.UserID) === String(userId));
    if (!u) return;
    setSelectedUsers((prev) =>
      prev.some((p) => String(p.id) === String(u.UserID))
        ? prev
        : [
            ...prev,
            {
              id: u.UserID,
              code: u.UserCode,
              desc: u.UserDescription || u.UserCode,
            },
          ],
    );
  };

  const addSelectedProject = (projectCode) => {
    if (!projectCode) return;
    const p = projectOptions.find((x) => x.code === projectCode);
    if (!p) return;
    setSelectedProjects((prev) =>
      prev.some((v) => v.code === p.code) ? prev : [...prev, p],
    );
  };

  const addSelectedActivity = (activityCode) => {
    if (!activityCode) return;
    const a = activityOptions.find((x) => x.code === activityCode);
    if (!a) return;
    setSelectedActivities((prev) =>
      prev.some((v) => v.code === a.code) ? prev : [...prev, a],
    );
  };

  const clearFilters = () => {
    setYear("");
    setStartDate("");
    setEndDate("");
    setSelectedUsers([]);
    setSelectedProjects([]);
    setSelectedActivities([]);
  };

  const exportCsv = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.exportDataAsCsv({
      fileName: `analytics-${year || "all"}.csv`,
    });
  };

  const renderFilterGroup = ({
    label,
    selected,
    placeholder,
    options,
    optionKey,
    optionValue,
    optionLabel,
    onAdd,
    onRemove,
  }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "90px minmax(260px, 1fr)",
        gap: 10,
        alignItems: "start",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, paddingTop: 9 }}>
        {label}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {selected.map((item) => (
          <span
            key={`${label}-${optionKey(item)}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#eef6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 999,
              padding: "4px 10px",
              minHeight: 30,
            }}
          >
            {optionLabel(item)}
            <button
              type="button"
              onClick={() => onRemove(item)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
              }}
            >
              x
            </button>
          </span>
        ))}
        <select
          defaultValue=""
          onChange={(e) => {
            if (!e.target.value) return;
            onAdd(e.target.value);
            e.target.value = "";
          }}
          style={{ minWidth: 240, minHeight: 30 }}
        >
          <option value="">{placeholder}</option>
          {options.map((item) => (
            <option
              key={`${label}-option-${optionKey(item)}`}
              value={optionValue(item)}
            >
              {optionLabel(item)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 20 }}>
      <h2>Analytics</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button
          onClick={() => setYear((value) => Number(value || defaultYear) - 1)}
        >
          -
        </button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(e.target.value === "" ? "" : Number(e.target.value))
          }
          style={{ width: 110 }}
        />
        <button
          onClick={() => setYear((value) => Number(value || defaultYear) + 1)}
        >
          +
        </button>
        <button
          onClick={exportCsv}
          disabled={loading || filteredRows.length === 0}
          style={{ marginLeft: 8 }}
        >
          Export CSV
        </button>
        <div
          ref={fieldMenuRef}
          style={{ marginLeft: 12, position: "relative" }}
        >
          <button onClick={() => setShowFieldMenu((v) => !v)} type="button">
            Customize Columns
          </button>
          {showFieldMenu ? (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                zIndex: 20,
                width: 240,
                maxHeight: 280,
                overflowY: "auto",
                background: "white",
                border: "1px solid #ddd",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                padding: 10,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                Available Fields
              </div>
              {fieldOptions.map((opt) => (
                <label
                  key={opt.field}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={visibleFields.includes(opt.field)}
                    onChange={() => toggleField(opt.field)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginBottom: 10, color: "#555", fontSize: 13 }}>
        {year
          ? `Current user's timesheet records for ${year}.`
          : "Current user's timesheet records across all available years."}
      </div>

      <div
        style={{
          marginBottom: 12,
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 8,
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 700 }}>Filters</div>
          <button type="button" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gap: 10,
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "90px minmax(260px, 1fr)",
                gap: 10,
                alignItems: "start",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, paddingTop: 9 }}>
                Dates
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="Start date"
                  style={{ minWidth: 160, minHeight: 30 }}
                />
                <span style={{ color: "#666", fontSize: 12 }}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="End date"
                  style={{ minWidth: 160, minHeight: 30 }}
                />
              </div>
            </div>

            {renderFilterGroup({
              label: "Users",
              selected: selectedUsers,
              placeholder: "Add user...",
              options: userOptions,
              optionKey: (item) => item.UserID || item.UserCode,
              optionValue: (item) => item.UserID,
              optionLabel: (item) =>
                item.desc || item.UserDescription
                  ? `${item.code || item.UserCode} - ${item.desc || item.UserDescription}`
                  : item.code || item.UserCode,
              onAdd: addSelectedUser,
              onRemove: (item) =>
                setSelectedUsers((prev) =>
                  prev.filter((x) => String(x.id) !== String(item.id)),
                ),
            })}

            {renderFilterGroup({
              label: "Projects",
              selected: selectedProjects,
              placeholder: "Add project...",
              options: projectOptions,
              optionKey: (item) => item.id || item.code,
              optionValue: (item) => item.code,
              optionLabel: (item) => `${item.code} - ${item.desc}`,
              onAdd: addSelectedProject,
              onRemove: (item) =>
                setSelectedProjects((prev) =>
                  prev.filter((x) => x.code !== item.code),
                ),
            })}

            {renderFilterGroup({
              label: "Activities",
              selected: selectedActivities,
              placeholder: "Add activity...",
              options: activityOptions,
              optionKey: (item) => item.id || item.code,
              optionValue: (item) => item.code,
              optionLabel: (item) => `${item.code} - ${item.desc}`,
              onAdd: addSelectedActivity,
              onRemove: (item) =>
                setSelectedActivities((prev) =>
                  prev.filter((x) => x.code !== item.code),
                ),
            })}
          </div>
        </div>
      </div>

      {error ? (
        <div style={{ color: "darkred", marginBottom: 8 }}>{error}</div>
      ) : null}

      <div style={{ width: "100%", overflowX: "auto" }}>
        <div
          className="ag-theme-balham client-support-grid"
          style={{ width: "100%", height: 520 }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={filteredRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            domLayout="normal"
          />
        </div>
      </div>
    </div>
  );
}
